(() => {
  const mobile = matchMedia('(max-width: 749px)');
  const desktopNav = matchMedia('(min-width: 990px)');
  const cleanups = new WeakMap();

  /* ---------- Announcement bar ---------- */
  function initAnnounce(root) {
    const items = [...root.querySelectorAll('.ap-announce__item')];
    if (!items.length) return () => {};
    let index = Math.max(0, items.findIndex(i => i.classList.contains('is-active')));
    let timer = null;
    let paused = false;
    const visible = () => items.filter(i => getComputedStyle(i).display !== 'none');
    const rotating = () => root.dataset.mode !== 'all' || mobile.matches;
    const show = target => {
      const list = visible();
      if (!list.length) return;
      if (!list.includes(items[index])) index = items.indexOf(list[0]);
      let next = target;
      if (typeof target === 'number') {
        const pos = (list.indexOf(items[index]) + target + list.length) % list.length;
        next = list[pos];
      }
      items.forEach(i => { i.classList.toggle('is-active', i === next); i.setAttribute('aria-hidden', i === next ? 'false' : 'true'); });
      index = items.indexOf(next);
    };
    const stop = () => { clearInterval(timer); timer = null; };
    const start = () => {
      stop();
      if (root.dataset.autoplay !== 'true' || !rotating() || visible().length < 2 || paused) return;
      timer = setInterval(() => show(1), (parseFloat(root.dataset.speed) || 5) * 1000);
    };
    const sync = () => {
      if (!rotating()) { stop(); items.forEach(i => i.removeAttribute('aria-hidden')); return; }
      show(0); start();
    };
    root.querySelector('[data-prev]')?.addEventListener('click', () => { show(-1); start(); });
    root.querySelector('[data-next]')?.addEventListener('click', () => { show(1); start(); });
    const pause = () => { paused = true; stop(); };
    const resume = () => { paused = false; start(); };
    root.addEventListener('mouseenter', pause);
    root.addEventListener('mouseleave', resume);
    root.addEventListener('focusin', pause);
    root.addEventListener('focusout', resume);
    root.addEventListener('shopify:block:select', e => { pause(); const item = e.target.closest('.ap-announce__item'); if (item) show(item); });
    root.addEventListener('shopify:block:deselect', resume);
    mobile.addEventListener('change', sync);
    sync();
    return () => { stop(); mobile.removeEventListener('change', sync); };
  }

  /* ---------- Header ---------- */
  function initHeader(header) {
    const ac = new AbortController();
    const opt = { signal: ac.signal };
    const drawer = header.querySelector('.ap-drawer');
    const search = header.querySelector('.ap-searchbox');
    const navItems = [...header.querySelectorAll('.ap-nav__item')];
    const lock = on => document.documentElement.classList.toggle('ap-lock', on);

    const setHeight = () => document.documentElement.style.setProperty('--ap-header-h', `${header.getBoundingClientRect().height}px`);
    const ro = new ResizeObserver(setHeight); ro.observe(header); setHeight();

    drawer?.addEventListener('toggle', () => {
      lock(drawer.open);
      if (drawer.open) { search && (search.open = false); drawer.querySelector('.ap-drawer__close')?.focus(); }
    }, opt);
    header.querySelectorAll('[data-drawer-close]').forEach(btn => btn.addEventListener('click', e => { e.preventDefault(); drawer.open = false; drawer.querySelector('summary')?.focus(); }, opt));
    search?.addEventListener('toggle', () => { if (search.open) search.querySelector('input')?.focus(); }, opt);

    navItems.forEach(item => {
      item.addEventListener('toggle', () => { if (item.open) navItems.forEach(o => { if (o !== item) o.open = false; }); }, opt);
      let t;
      item.addEventListener('mouseenter', () => { if (!matchMedia('(hover: hover)').matches) return; clearTimeout(t); t = setTimeout(() => { item.open = true; }, 90); }, opt);
      item.addEventListener('mouseleave', () => { if (!matchMedia('(hover: hover)').matches) return; clearTimeout(t); t = setTimeout(() => { item.open = false; }, 160); }, opt);
    });
    document.addEventListener('click', e => {
      if (!header.contains(e.target)) { navItems.forEach(i => { i.open = false; }); if (search) search.open = false; }
    }, opt);
    document.addEventListener('keydown', e => {
      if (e.key !== 'Escape') return;
      if (drawer?.open) { drawer.open = false; drawer.querySelector('summary')?.focus(); return; }
      const open = navItems.find(i => i.open);
      if (open) { open.open = false; open.querySelector('summary')?.focus(); }
      if (search?.open) { search.open = false; search.querySelector('summary')?.focus(); }
    }, opt);
    const onBp = () => { if (desktopNav.matches && drawer?.open) drawer.open = false; };
    desktopNav.addEventListener('change', onBp);

    import('@shopify/events').then(({ StandardEvents }) => {
      document.addEventListener(StandardEvents.cartLinesUpdate, event => {
        event.promise?.then(({ cart, detail }) => {
          const count = cart?.totalQuantity ?? detail?.itemCount;
          if (Number.isInteger(count)) header.querySelectorAll('[data-cart-count]').forEach(n => { n.textContent = String(count); n.dataset.count = String(count); });
        }).catch(() => {});
      }, opt);
    }).catch(() => {});

    return () => { ac.abort(); ro.disconnect(); lock(false); desktopNav.removeEventListener('change', onBp); };
  }

  /* ---------- Footer ---------- */
  function initFooter(footer) {
    const groups = [...footer.querySelectorAll('.ap-fcol > details')];
    const sync = () => {
      const collapse = mobile.matches && footer.dataset.mobile === 'accordion' && !window.Shopify?.designMode;
      groups.forEach(d => { d.open = !collapse; });
    };
    groups.forEach(d => d.querySelector('summary')?.addEventListener('click', e => {
      if (!(mobile.matches && footer.dataset.mobile === 'accordion')) e.preventDefault();
    }));
    sync();
    mobile.addEventListener('change', sync);
    return () => mobile.removeEventListener('change', sync);
  }

  const kinds = [['[data-ap-announce]', initAnnounce], ['[data-ap-header]', initHeader], ['[data-ap-footer]', initFooter]];
  const bind = (scope = document) => {
    kinds.forEach(([sel, fn]) => scope.querySelectorAll(sel).forEach(node => {
      if (cleanups.has(node)) return;
      cleanups.set(node, fn(node));
    }));
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => bind()); else bind();
  document.addEventListener('shopify:section:load', e => bind(e.target));
  document.addEventListener('shopify:section:unload', e => {
    kinds.forEach(([sel]) => e.target.querySelectorAll(sel).forEach(node => { cleanups.get(node)?.(); cleanups.delete(node); }));
  });
  document.addEventListener('shopify:block:select', e => {
    let node = e.target;
    while (node && node !== document.body) {
      if (node instanceof HTMLDetailsElement && !node.classList.contains('ap-nav__item')) node.open = true;
      node = node.parentElement;
    }
  });
})();
